import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const SpeakerMediaPage = () => {
  const { speakerProfile, setSpeakerProfile } = useOutletContext();
  const [saving, setSaving] = useState(false);
  const [videoId, setVideoId] = useState(speakerProfile?.youtube_video_id || '');
  const [additionalVideos, setAdditionalVideos] = useState(speakerProfile?.additional_videos || []);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const extractVideoId = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    return match ? match[1] : url;
  };

  const handleSaveFeaturedVideo = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('speakers')
        .update({ youtube_video_id: videoId })
        .eq('id', speakerProfile.id);
      if (error) throw error;
      setSpeakerProfile({ ...speakerProfile, youtube_video_id: videoId });
      showToast('Featured video updated!');
    } catch (error) {
      showToast('Failed to save: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddVideo = async () => {
    if (!newVideoUrl.trim()) return;
    const id = extractVideoId(newVideoUrl);
    if (additionalVideos.includes(id)) {
      showToast('Video already added', 'error');
      return;
    }
    const updated = [...additionalVideos, id];
    setSaving(true);
    try {
      const { error } = await supabase
        .from('speakers')
        .update({ additional_videos: updated })
        .eq('id', speakerProfile.id);
      if (error) throw error;
      setAdditionalVideos(updated);
      setSpeakerProfile({ ...speakerProfile, additional_videos: updated });
      setNewVideoUrl('');
      showToast('Video added!');
    } catch (error) {
      showToast('Failed to add video', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveVideo = async (id) => {
    const updated = additionalVideos.filter(v => v !== id);
    setSaving(true);
    try {
      const { error } = await supabase
        .from('speakers')
        .update({ additional_videos: updated })
        .eq('id', speakerProfile.id);
      if (error) throw error;
      setAdditionalVideos(updated);
      setSpeakerProfile({ ...speakerProfile, additional_videos: updated });
      showToast('Video removed');
    } catch (error) {
      showToast('Failed to remove video', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast.show && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-xl shadow-lg ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white`}>
          {toast.message}
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Media Gallery</h1>
        <p className="mt-1 text-sm text-gray-500">Manage your videos and media content.</p>
      </div>

      {/* Featured Video */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"/><path fill="white" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          Featured Video
        </h2>
        <p className="text-sm text-gray-500 mb-4">This video appears prominently on your public profile.</p>
        
        <div className="space-y-4">
          <div className="flex gap-3">
            <input
              type="text"
              value={videoId}
              onChange={(e) => setVideoId(e.target.value)}
              placeholder="YouTube Video ID (e.g., dQw4w9WgXcQ)"
              className="flex-1 px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
            />
            <button
              onClick={handleSaveFeaturedVideo}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
          
          {videoId && (
            <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${videoId}`}
                title="Featured Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>
      </div>

      {/* Additional Videos */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Additional Videos</h2>
        <p className="text-sm text-gray-500 mb-4">Add more sermon clips or speaking samples.</p>

        <div className="flex gap-3 mb-6">
          <input
            type="text"
            value={newVideoUrl}
            onChange={(e) => setNewVideoUrl(e.target.value)}
            placeholder="YouTube URL or Video ID"
            className="flex-1 px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
          />
          <button
            onClick={handleAddVideo}
            disabled={saving || !newVideoUrl.trim()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            Add Video
          </button>
        </div>

        {additionalVideos.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
            <p>No additional videos yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {additionalVideos.map((id, index) => (
              <div key={index} className="relative group">
                <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${id}`}
                    title={`Video ${index + 1}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <button
                  onClick={() => handleRemoveVideo(id)}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800">
        <div className="flex">
          <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <div>
            <h4 className="font-medium text-blue-800 dark:text-blue-300">Video Tips</h4>
            <ul className="text-sm text-blue-600 dark:text-blue-400 mt-1 space-y-1">
              <li>• Use high-quality recordings that showcase your speaking style</li>
              <li>• Include a variety of topics to show your range</li>
              <li>• Keep videos between 5-15 minutes for best engagement</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeakerMediaPage;
